import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input, output, signal,
  AfterViewInit, effect, HostListener, DestroyRef
} from '@angular/core';
import { UpperCasePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbDate, NgbDateParserFormatter, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { FADE } from '../../../../common/animations/animations';
import { ButtonComponent } from '../../../../common/components/button/button.component';
import { SkeletonLoaderComponent } from '../../../../common/components/skeleton-loader/skeleton-loader';
import { TagComponent } from '../../../../common/components/tag/tag.component';
import { TextboxComponent } from '../../../../common/components/textbox/textbox.component';
import {
  CAL_LOADER_CONFIG,
  CAL_LOADER_CONFIG_BUTTON,
  DATE_INPUT_FORMAT_REGEX
} from '../../../../common/configs/common.config';
import { CustomPriceFormatPipe } from '../../../../common/pipes/priceFormat.pipe';
import { SafeHtml } from '../../../../common/pipes/SafeHtml.pipe';
import { CalendarService } from '../../../../common/services/calendar.service';
import { CommonService } from '../../../../common/services/common.service';
import { ShoppingCartService } from '../../../../common/services/shopping-cart.service';
import { StoreService } from '../../../../store/store.service';
import { filter } from 'rxjs/operators';
import { DateTime } from 'luxon';

@Component({
  standalone: true,
  imports: [
    SafeHtml,
    CustomPriceFormatPipe,
    UpperCasePipe,
    TagComponent,
    ButtonComponent,
    SkeletonLoaderComponent,
    TextboxComponent,
    NgClass,
    NgTemplateOutlet,
],
  selector: 'app-calendar-summary',
  templateUrl: './calendar-summary.component.html',
  styleUrl: './calendar-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [FADE],
  host: {
    '[@fade]': 'true',
  },
})

export class CalendarSummaryComponent implements AfterViewInit {
  // Internal signals for skeleton loader management
  private readonly loadingStartTime = signal<number | null>(null);
  private readonly animationFrameId = signal<number | null>(null);

  // Optional Inputs
  public readonly mobileScreen = input<boolean>();
  public readonly hideDateInputs = input<boolean>(false);
  public readonly hideSummary = input<boolean>(false);
  public readonly isSelectRoomInProgress = input<boolean>(false);
  public readonly isCalendarSidepanel = input<boolean>(false);

  // Outputs
  public readonly onSameDateError = output<void>();
  public readonly onAction = output<'SELECT_ROOM' | 'VIEW_ALTERNATE_PROPERTIES'>();

  // Properties
  public loaderData: { [key: string]: any } = CAL_LOADER_CONFIG;
  public loaderDataButton: { [key: string]: any } = CAL_LOADER_CONFIG_BUTTON;

  // Signals
  public readonly localeObj = this.storeSvc.userStore.localeObj;
  public readonly showTaxesInDisplayPrice = this.storeSvc.userStore.propertyInfo.showTaxesInDisplayPrice;
  public readonly includeFeesOnly = this.storeSvc.userStore.propertyInfo.includeFeesOnly;
  public readonly isLoading = this.calendarSvc.isStayDayRatesLoading;
  public readonly rateQuoteData = this.calendarSvc.stayDatesData;
  public readonly currCode = this.storeSvc.userStore.currency.code;
  public readonly currSymbol = this.storeSvc.userStore.currency.symbol;
  public readonly isLoyaltyEnabledProperty = this.storeSvc.userStore.propertyInfo?.isLoyaltyEnabled;
  public readonly isLoyaltyMemberSet = this.storeSvc.appStore.isLoyaltyMemberSet;
  public readonly loyaltyIconURL = this.storeSvc.userStore.propertyInfo.loyaltyIcon;
  public readonly complimentaryIconURL = this.storeSvc.userStore.propertyInfo.complimentaryIcon;
  public readonly showAverageNightlyRate = this.storeSvc.userStore.propertyInfo.showAverageNightlyRate;
  public readonly showPriceRateBreakdown = this.storeSvc.userStore.propertyInfo.showPriceRateBreakdown;
  public readonly displayRoundedPrices = this.storeSvc.userStore.propertyInfo.displayRoundedPrices;
  public readonly isUpperCaseLabel = this.storeSvc.appStore.isUpperCaseLabel;
  public readonly isDayUseRoomsEnabled = this.storeSvc.appStore.isDayUseRoomsEnabled;
  public readonly isStayDateSelectionInProgress = this.calendarSvc.isStayDateSelectionInProgress;
  public readonly isAltPropertyAvailable = this.calendarSvc.isAltPropertyAvailable;
  public readonly isStartDateEditingStarted = signal(false);
  public readonly isEndDateEditingStarted = signal(false);
  public readonly startDateInput = signal('');
  public readonly startDateError = signal<string>('');
  public readonly endDateInput = signal('');
  public readonly endDateError = signal<string>('');
  public readonly showSkeletonLoader = signal(false);
  public readonly isDesktopLarge = computed(() => this.storeSvc.res().desktopLarge);

  // Computed Signals
  public readonly hasStayDates = computed(() => {
    const { fromDate, toDate } = this.calendarSvc.stayDates();
    const fromDateLuxon = DateTime.fromFormat(`${fromDate?.year}-${fromDate?.month}-${fromDate?.day}`, 'yyyy-M-d');
    const toDateLuxon = DateTime.fromFormat(`${toDate?.year}-${toDate?.month}-${toDate?.day}`, 'yyyy-M-d');

    return fromDateLuxon.isValid && toDateLuxon.isValid;
  });
  public readonly LOS = computed(() => {
    const { fromDate, toDate } = this.calendarSvc.stayDates();

    const fromDateLuxon = DateTime.fromFormat(`${fromDate?.year}-${fromDate?.month}-${fromDate?.day}`, 'yyyy-M-d');
    const toDateLuxon = DateTime.fromFormat(`${toDate?.year}-${toDate?.month}-${toDate?.day}`, 'yyyy-M-d');

    return toDateLuxon.diff(fromDateLuxon, 'days').days;
  });
  public readonly taxLabel = computed(() => {
    const rateQuoteData = this.rateQuoteData();
    const currCode = this.currCode();

    return this.shoppingCartSvc.taxDisplayForCalendars(
      rateQuoteData,
      currCode,
      rateQuoteData?.totalFeeTypeTaxDisplay,
      rateQuoteData?.totalTaxAndFeesExcluded,
    );
  });

  constructor(
    public readonly calendarSvc: CalendarService,
    public readonly storeSvc: StoreService,
    public readonly commonSvc: CommonService,
    public readonly ngbDateParserFormatterSvc: NgbDateParserFormatter,
    public readonly shoppingCartSvc: ShoppingCartService,
    public readonly destroyRefSvc: DestroyRef,
  ) {
    this.calendarSvc.checkinSummaryDateInputSaved$
      .pipe(
        takeUntilDestroyed(this.destroyRefSvc),
        filter(source => source !== null)
      )
      .subscribe((source) => {
        if (source === 'CHECK_IN_INPUT') {
          this.isStartDateEditingStarted.set(false);
          this.startDateEditing('CHECK_OUT');
          this.calendarSvc.checkinSummaryDateInputSaved$.next(null);
        } else if (source === 'CHECK_OUT_INPUT') {
          this.isEndDateEditingStarted.set(false);
          this.calendarSvc.checkinSummaryDateInputSaved$.next(null);
        }
      });

    // Monitor loading state - button only renders when not loading
    effect(() => {
      if (this.mobileScreen()) {
        const isLoading = this.isLoading();
        if (!isLoading) {
          // Button should be rendered now, check sticky state
          setTimeout(() => this.handleStickyButton(), 200);
        }
      }
    });

    // Manage delayed skeleton loader
    this.setupSkeletonLoaderEffect();
  }

  ngAfterViewInit() {
    if (this.mobileScreen()) {
      setTimeout(() => this.handleStickyButton(), 200);
    }
  }

  private setupSkeletonLoaderEffect(): void {
    // Track loading state changes
    effect(() => {
      const isLoading = this.isLoading();

      if (isLoading) {
        // Record when loading started
        if (!this.loadingStartTime()) {
          this.loadingStartTime.set(performance.now());
          this.startSkeletonAnimation();
        }
      } else {
        // Loading stopped - clean up
        this.cancelSkeletonAnimation();
        this.loadingStartTime.set(null);
        this.showSkeletonLoader.set(false);
      }
    });
  }

  private startSkeletonAnimation(): void {
    const checkAndShow = () => {
      const startTime = this.loadingStartTime();
      if (!startTime || !this.isLoading()) {
        return; // Loading stopped, exit
      }

      const elapsed = performance.now() - startTime;

      if (elapsed >= 500) {
        // 500ms have passed and still loading
        if (this.isLoading()) {
          this.showSkeletonLoader.set(true);
        }
      } else {
        // Continue checking
        const frameId = requestAnimationFrame(checkAndShow);
        this.animationFrameId.set(frameId);
      }
    };

    const frameId = requestAnimationFrame(checkAndShow);
    this.animationFrameId.set(frameId);
  }

  private cancelSkeletonAnimation(): void {
    const frameId = this.animationFrameId();
    if (frameId !== null) {
      cancelAnimationFrame(frameId);
      this.animationFrameId.set(null);
    }
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    if (this.mobileScreen()) {
      this.handleStickyButton();
    }
  }

  private readonly handleStickyButton = () => {
    const button = document.querySelector('.select-room-button') as HTMLElement;
    const footer = document.querySelector('footer');
    if (!button || !footer) return;

    const footerRect = footer.getBoundingClientRect();
    const footerVisible = footerRect.top <= window.innerHeight;

    if (footerVisible) {
      button.classList.remove(
        'add-sticky-button', 'start-0', 'end-0', 'bottom-0', 'p-4', 'position-fixed', 'neutral-white-color-bg'
      );
    } else {
      button.classList.add(
        'add-sticky-button', 'start-0', 'end-0', 'bottom-0', 'p-4', 'position-fixed', 'neutral-white-color-bg'
      );
    }
  };

  // Starts the editing of the checkIn or checkOut date input field
  public startDateEditing(type: 'CHECK_IN' | 'CHECK_OUT') {
    if (type === 'CHECK_IN') {
      this.isStartDateEditingStarted.set(true);
      setTimeout(() => {
        (document.getElementById('startDateInput') as HTMLElement)?.focus();
      }, 100);

      if (this.isEndDateEditingStarted()) {
        this.endDateInput.set('');
        this.endDateError.set('');
        this.isEndDateEditingStarted.set(false);
      }
    } else {
      this.isEndDateEditingStarted.set(true);
      setTimeout(() => {
        (document.getElementById('endDateInput') as HTMLElement)?.focus();
      }, 100);
    }
  }

  // Handles the date input in the checkIn or checkOut date input field
  public appendSlashToValue(value: string) {
    if ([2, 5]?.includes(value?.length)) {
      return value + '/';
    } else {
      return value;
    }
  };

  public handleKeyUp(type: 'CHECK_IN' | 'CHECK_OUT', e: KeyboardEvent) {
    'preventDefault' in e && e?.preventDefault();
    const value = (e.target as HTMLInputElement)?.value ?? '';
    const isDateValid = this.isValidDate(value);

    if (value.length === 10 && !isDateValid) {
      this.setStayDateErrorMessage(type, 'VALIDATION');
    } else if (value.length === 10 && isDateValid) {
      this.setStayDateErrorMessage(type, 'EMPTY');
    }

    if (
      isDateValid && ['Enter', 'Tab'].includes(e.key) && (
        type === 'CHECK_IN' && this.isStartDateEditingStarted() ||
        type === 'CHECK_OUT' && this.isEndDateEditingStarted()
      )
    ) {
      if (type === 'CHECK_IN') {
        this.startDateInput.set(value);
      } else {
        this.endDateInput.set(value);
      }
      this.endDateEditing(type, value);
    } else if (e.key === 'Escape' && type === 'CHECK_IN' && !this.calendarSvc.isStayDateSelectionInProgress()) {
      this.setStayDateErrorMessage('CHECK_IN', 'EMPTY');
      this.isStartDateEditingStarted.set(false);
    } else if (e.key === 'Escape' && type === 'CHECK_OUT' && !this.calendarSvc.isStayDateSelectionInProgress()) {
      this.setStayDateErrorMessage('CHECK_OUT', 'EMPTY');
      this.isEndDateEditingStarted.set(false);
    } else if (/[0-9\/]/.test(e.key)) {
      if (type === 'CHECK_IN') {
        this.startDateInput.set(this.appendSlashToValue(value));
      } else {
        this.endDateInput.set(this.appendSlashToValue(value));
      }
    } else if (
      value.length > 1 && value.length <= 10 && !/[0-9\/]/.test(value) ||
      value.length === 10 && !isDateValid
    ) {
      this.setStayDateErrorMessage(type, 'VALIDATION');
    } else {
      this.setStayDateErrorMessage(type, 'EMPTY');
    }
  }

  // Ends the editing of the checkIn or checkOut date input field and validates the entered dates
  public endDateEditing(dateType: 'CHECK_IN' | 'CHECK_OUT', dateString: string) {
    const isDateValid = this.isValidDate(dateString);

    if (!isDateValid) {
      this.setStayDateErrorMessage(dateType, 'VALIDATION');
      return;
    }

    this.setStayDateErrorMessage(dateType, 'EMPTY');
    const startDate = this.ngbDateParserFormatterSvc.parse(this.startDateInput()) as NgbDateStruct;

    if (dateType === 'CHECK_IN') {
      const startDateNgb = NgbDate.from(startDate) as NgbDate;

      if (startDateNgb) {
        // Update the check in date on the calendar
        this.calendarSvc.checkinSummaryDateInputUpdated$.next({ date: startDateNgb, source: 'CHECK_IN_INPUT' });
      }
    } else {
      const fromDate = this.calendarSvc.stayDates()?.fromDate;
      const toDate = this.ngbDateParserFormatterSvc.parse(this.endDateInput()) as NgbDateStruct;

      const startDateLuxon: DateTime<boolean> = DateTime.fromFormat(
        startDate
          ? `${startDate?.year}-${startDate?.month}-${startDate?.day}`
          : `${fromDate?.year}-${fromDate?.month}-${fromDate?.day}`,
        'yyyy-M-d'
      );
      const endDateLuxon: DateTime<boolean> = DateTime.fromFormat(
        `${toDate?.year}-${toDate?.month}-${toDate?.day}`,
        'yyyy-M-d'
      );

      // Check if the start date is the same as the end date and emit the same date error
      if (startDateLuxon.isValid && endDateLuxon.isValid && startDateLuxon.hasSame(endDateLuxon, 'day')) {
        this.onSameDateError.emit();
        return;
      }

      if (toDate) {
        // Update the check in and check out dates on the calendar
        this.calendarSvc.updateStayDates.next({
          fromDate: startDate || fromDate,
          toDate,
          source: 'CHECK_OUT_INPUT',
        });
      }
    }
  }

  // validate the give dates in the inpu fields of checkIn CheckOut
  public isValidDate(dateString: string) {
    if (
      !dateString ||
      (dateString?.length <= 10 && !DATE_INPUT_FORMAT_REGEX.test(dateString)) ||
      (dateString?.length === 10 && !DATE_INPUT_FORMAT_REGEX.test(dateString))
    ) {
      return false;
    }

    const minDate = this.calendarSvc.minDate();
    const enteredLuxon: DateTime = DateTime.fromFormat(dateString, 'MM/dd/yyyy');
    const minDateLuxon: DateTime = DateTime.fromFormat(
      `${minDate?.year}-${minDate?.month}-${minDate?.day}`,
      'yyyy-M-d'
    );

    // Check if the entered date is after the minimum date
    return enteredLuxon.isValid && minDateLuxon.isValid && enteredLuxon >= minDateLuxon;
  }

  public setStayDateErrorMessage(dateType: 'CHECK_IN' | 'CHECK_OUT', errorType: 'VALIDATION' | 'EMPTY') {
    if (errorType === 'VALIDATION') {
      const errorMessage = this.localeObj()?.tf_1_Calendar_rateCalender_dateFormat_error;
      dateType === 'CHECK_IN'
        ? this.startDateError.set(errorMessage)
        : this.endDateError.set(errorMessage);
    } else {
      dateType === 'CHECK_IN'
        ? this.startDateError.set('')
        : this.endDateError.set('');
    }
  }

  public getTranslatedDate(type: 'CHECK_IN' | 'CHECK_OUT') {
    const isUsBasedDateFormat = this.commonSvc.isUsBasedDateFormat(
      this.storeSvc.userStore.propertyInfo()?.propertyDefaultDateFormat
    );

    if (type === 'CHECK_IN') {
      if (!this.isStayDateSelectionInProgress()) {
        const { fromDate } = this.calendarSvc.stayDates();
        const dateLuxon: DateTime<boolean> = DateTime.fromFormat(
          `${fromDate?.year}-${fromDate?.month}-${fromDate?.day}`,
          'yyyy-M-d'
        );
        return this.commonSvc.getFormattedDateByLocale(
          dateLuxon.toFormat('yyyy-MM-dd'),
          this.localeObj(),
          isUsBasedDateFormat,
          true,
          false
        );
      } else {
        const { fromDate } = this.calendarSvc.priorStayDates();
        const dateLuxon: DateTime<boolean> = DateTime.fromFormat(
          `${fromDate?.year}-${fromDate?.month}-${fromDate?.day}`,
          'yyyy-M-d'
        );
        return this.commonSvc.getFormattedDateByLocale(
          dateLuxon.toFormat('yyyy-MM-dd'),
          this.localeObj(),
          isUsBasedDateFormat,
          true,
          false
        );
      }
    } else {
      const endDateInput = this.endDateInput();
      const isEndDateInputValid = endDateInput?.length === 10;

      if (this.isStayDateSelectionInProgress() && isEndDateInputValid && this.isValidDate(endDateInput)) {
        const dateLuxon: DateTime<boolean> = DateTime.fromFormat(
          endDateInput,
          'MM/dd/yyyy'
        );
        return this.commonSvc.getFormattedDateByLocale(
          dateLuxon.toFormat('yyyy-MM-dd'),
          this.localeObj(),
          isUsBasedDateFormat,
          true,
          false
        );
      } else {
        const { toDate } = this.calendarSvc.priorStayDates();
        const dateLuxon: DateTime<boolean> = DateTime.fromFormat(
          `${toDate?.year}-${toDate?.month}-${toDate?.day}`,
          'yyyy-M-d'
        );
        return this.commonSvc.getFormattedDateByLocale(
          dateLuxon.toFormat('yyyy-MM-dd'),
          this.localeObj(),
          isUsBasedDateFormat,
          true,
          false
        );
      }
    }
  }

  public priceRateDisplayString(room: any, priceObj: any) {
    let priceRateString = '';

    if (
      this.showPriceRateBreakdown() &&
      !(this.hasPartialRatePlan(room?.bedTypes) || room?.hasComplimentaryNight || room?.includeComplimentaryNight)
    ) {
      const params = [
        this.currSymbol,
        this.displayRoundedPrices()
          ? priceObj?.roomPriceDisplay?.toFixed(0)
          : priceObj?.roomPriceDisplay?.toFixed(2),
        this.displayRoundedPrices()
          ? priceObj?.totalFeeTypeTaxDisplay?.toFixed(0)
          : priceObj?.totalFeeTypeTaxDisplay?.toFixed(2),
      ];
      if (!this.showTaxesInDisplayPrice()) {
        priceRateString = '';
      } else if (this.includeFeesOnly() && priceObj?.totalFeeTypeTaxDisplay) {
        priceRateString = this.commonSvc.fillMessage(this.localeObj()?.tf_2_RoomList_roomRateFeesOnly, params);
      } else if (this.showTaxesInDisplayPrice() && !this.includeFeesOnly()) {
        if (priceObj?.totalTaxTypeTaxDisplay && priceObj?.totalFeeTypeTaxDisplay) {
          priceRateString = this.commonSvc.fillMessage(this.localeObj()?.tf_2_RoomList_roomRateTotal, [
            ...params,
            this.displayRoundedPrices()
              ? priceObj?.totalTaxTypeTaxDisplay?.toFixed(0)
              : priceObj?.totalTaxTypeTaxDisplay?.toFixed(2),
          ]);
        } else if (priceObj?.totalFeeTypeTaxDisplay) {
          priceRateString = this.commonSvc.fillMessage(this.localeObj()?.tf_2_RoomList_roomRateFeesOnly, params);
        }
      }
    }

    let taxDisplayForPriceRate;
    if (this.includeFeesOnly() && this.showTaxesInDisplayPrice()) {
      taxDisplayForPriceRate = this.localeObj()?.tf_5_Confirmation_bookingInfo_inclusiveFees;
    } else if (this.showTaxesInDisplayPrice()) {
      taxDisplayForPriceRate = this.localeObj()?.tf_5_Confirmation_bookingInfo_inclusiveTaxes;
    } else {
      taxDisplayForPriceRate = this.localeObj()?.tf_2_RoomList_rooms_excludesFeesTaxes;
    }

    return (taxDisplayForPriceRate || '') + ('<br>' + (priceRateString || ''));
  };

  public hasPartialRatePlan = (bedTypes: any[]) => {
    let isPartialRatePlan = false;
    bedTypes &&
      bedTypes.forEach((e) => !e?.isFullyComplimentary && e?.hasComplimentaryNight && (isPartialRatePlan = true));
    return isPartialRatePlan;
  };

  public handleAction(action: 'SELECT_ROOM' | 'VIEW_ALTERNATE_PROPERTIES') {
    this.onAction.emit(action);
  }
}
